import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { FiArrowLeft, FiMaximize, FiMinimize, FiRefreshCw } from 'react-icons/fi';
import './PowerHourPage.css';
import './PowerHourCoins.css';

interface CoinRep {
  name: string;
  coins: number;
  dials: number;
  connects: number;
  meetings: number;
  streak?: number;
}

interface PeriodBoard {
  period: string;
  coins: number;
  dials: number;
  connects: number;
  meetings: number;
  reps: CoinRep[];
}

interface BonusAward {
  id: string;
  name: string;
  coins: number;
  kind?: string;
  label?: string;
  note?: string;
  at: string;
}

interface MarketRep {
  name: string;
  earned_season: number;
  held_spent: number;
  spendable: number;
  meetings_season: number;
  fuel?: number;
}

interface MarketItem {
  key: string;
  code?: string;
  redeem_command?: string;
  label: string;
  coins: number;
  teams: string[];
  requires_approval: boolean;
  poolable: boolean;
  stock_per_month: number;
  stock_left: number | null;
  pool_total: number | null;
}

interface MarketRecent {
  name: string;
  label: string;
  coins: number;
  kind: string;
  status: string;
  at: string;
}

interface MarketSummary {
  enabled: boolean;
  season: string;
  pilot_reps: string[];
  catalog: MarketItem[];
  reps: MarketRep[];
  redeemed_total: number;
  recent: MarketRecent[];
}

interface TVPayload {
  all_time?: CoinRep[];
  periods?: Record<string, PeriodBoard> & { error?: string };
  bonus_awards?: BonusAward[];
  on_floor?: string[];
  boosted?: string[];
  server_time?: string;
  error?: string;
}

interface VaultRules {
  earning?: Record<string, number>;
  multipliers?: { name: string; days_label: string; start: string; end: string; multiplier: number }[];
  boosts?: { enabled: boolean; multiplier: number; max_total_multiplier: number };
}

type Window = 'day' | 'week' | 'month' | 'all';

const POLL_MS = 10000;

const WINDOWS: { key: Window; label: string; sub: string }[] = [
  { key: 'day', label: 'TODAY', sub: 'minted today' },
  { key: 'week', label: 'THIS WEEK', sub: 'minted this week' },
  { key: 'month', label: 'THIS MONTH', sub: 'minted this month' },
  { key: 'all', label: 'CAREER', sub: 'lifetime earned' },
];

const initials = (name: string) =>
  name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

// deterministic vivid color per rep — same rule as the live board
const repHue = (name: string) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
};

const fmt = (n: number) => n.toLocaleString();

const ago = (iso: string) => {
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const PowerHourCoins: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<TVPayload | null>(null);
  const [market, setMarket] = useState<MarketSummary | null>(null);
  const [rules, setRules] = useState<VaultRules | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [win, setWin] = useState<Window>('all');
  const [fullscreen, setFullscreen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  // last seen career total per rep — drives the "+N" flash when coins land
  const prevRef = useRef<Record<string, number>>({});
  const [bumps, setBumps] = useState<Record<string, number>>({});

  const load = useCallback(async () => {
    fetch(`${API_BASE_URL}/api/power-hour/market/summary`)
      .then(r => r.json()).then(setMarket).catch(() => {});
    fetch(`${API_BASE_URL}/api/power-hour/rules`)
      .then(r => r.json()).then(setRules).catch(() => {});
    try {
      const res = await fetch(`${API_BASE_URL}/api/power-hour/tv`);
      const d: TVPayload = await res.json();
      if (d.error) { setErr(d.error); return; }
      setErr(null);
      // diff career totals so a landed meeting/connect visibly pops
      const next: Record<string, number> = {};
      const fresh: Record<string, number> = {};
      (d.all_time || []).forEach(r => {
        next[r.name] = r.coins;
        const before = prevRef.current[r.name];
        if (before !== undefined && r.coins > before) fresh[r.name] = r.coins - before;
      });
      const hadBaseline = Object.keys(prevRef.current).length > 0;
      prevRef.current = next;
      setData(d);
      setPulse(true);
      setTimeout(() => setPulse(false), 700);
      if (hadBaseline && Object.keys(fresh).length) {
        setBumps(fresh);
        setTimeout(() => setBumps({}), 4000);
      }
    } catch (e: any) {
      setErr(e?.message || 'failed to reach the vault');
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else pageRef.current?.requestFullscreen?.();
  };

  const career = useMemo(() => data?.all_time || [], [data]);
  const careerBy = useMemo(() => {
    const m: Record<string, CoinRep> = {};
    career.forEach(r => { m[r.name] = r; });
    return m;
  }, [career]);

  // the ranked list for the selected window, always carrying the career total
  const rows = useMemo(() => {
    if (win === 'all') return career;
    const board = data?.periods?.[win];
    const reps = (board?.reps || []).filter(r => r.coins > 0);
    return [...reps].sort((a, b) => b.coins - a.coins || b.meetings - a.meetings);
  }, [win, career, data]);

  const holders = career.length;
  const circulating = career.reduce((s, r) => s + r.coins, 0);
  const top = rows.length ? rows[0].coins : 0;
  const windowMeta = WINDOWS.find(w => w.key === win)!;
  const minted = win === 'all' ? circulating : (data?.periods?.[win]?.coins ?? 0);
  const onFloor = new Set(data?.on_floor || []);
  const boosted = new Set(data?.boosted || []);

  const jackpots = (data?.bonus_awards || []).filter(a => a.coins > 0);

  const walletBy = useMemo(() => {
    const m: Record<string, MarketRep> = {};
    (market?.reps || []).forEach(r => { m[r.name] = r; });
    return m;
  }, [market]);

  return (
    <div className="ph-page ph-coins" ref={pageRef}>
      <div className="ph-topbar">
        <div className="ph-logo">🪙 THE VAULT</div>
        <div className="ph-topbar-actions">
          <span className={`phc-live ${pulse ? 'phc-live-on' : ''}`}>
            <i /> LIVE
          </span>
          <button className="ph-mini-cta" onClick={() => navigate('/power-hour')}>🏔️ BOARD</button>
          <button className="ph-mini-cta" onClick={() => navigate('/power-hour/tv')}>📺 TV</button>
          <button className="ph-icon-btn" onClick={load} title="Refresh now"><FiRefreshCw /></button>
          <button className="ph-icon-btn" onClick={toggleFullscreen} title="Fullscreen">
            {fullscreen ? <FiMinimize /> : <FiMaximize />}
          </button>
          <button className="ph-icon-btn" onClick={() => navigate('/power-hour')} title="Back">
            <FiArrowLeft />
          </button>
        </div>
      </div>

      {err && <div className="phc-error">⚠️ {err}</div>}

      <div className="phc-treasury">
        <div className="phc-stat phc-stat-hero">
          <span className="phc-stat-label">COINS IN CIRCULATION</span>
          <span className="phc-stat-val">🪙 {fmt(circulating)}</span>
          <span className="phc-stat-foot">lifetime, across {holders} holders</span>
        </div>
        <div className="phc-stat">
          <span className="phc-stat-label">{windowMeta.label} MINT</span>
          <span className="phc-stat-val">{fmt(minted)}</span>
          <span className="phc-stat-foot">{windowMeta.sub}</span>
        </div>
        <div className="phc-stat">
          <span className="phc-stat-label">REDEEMED</span>
          <span className={`phc-stat-val${market?.redeemed_total ? '' : ' phc-muted'}`}>
            {fmt(market?.redeemed_total ?? 0)}
          </span>
          <span className="phc-stat-foot">
            {market?.enabled ? `held/spent this quarter (${market.season})` : 'marketplace not open yet'}
          </span>
        </div>
        <div className="phc-stat">
          <span className="phc-stat-label">AVG HOLDER</span>
          <span className="phc-stat-val">{fmt(holders ? Math.round(circulating / holders) : 0)}</span>
          <span className="phc-stat-foot">lifetime earned</span>
        </div>
      </div>

      <div className="phc-tabs">
        {WINDOWS.map(w => (
          <button
            key={w.key}
            className={`phc-tab ${win === w.key ? 'phc-tab-on' : ''}`}
            onClick={() => setWin(w.key)}
          >{w.label}</button>
        ))}
      </div>

      <div className="phc-body">
        <div className="phc-list">
          {!data && !err && <div className="phc-empty">Opening the vault…</div>}
          {data && rows.length === 0 && (
            <div className="phc-empty">No coins minted in this window yet.</div>
          )}
          {rows.map((r, i) => {
            const hue = repHue(r.name);
            const lifetime = careerBy[r.name]?.coins ?? r.coins;
            const share = top > 0 ? (r.coins / top) * 100 : 0;
            const bump = bumps[r.name];
            return (
              <div className={`phc-row ${i < 3 ? `phc-row-${i + 1}` : ''}`} key={r.name}>
                <div className="phc-rank">{i + 1}</div>
                <div
                  className="phc-avatar"
                  style={{
                    background: `linear-gradient(135deg, hsl(${hue} 80% 55%), hsl(${(hue + 40) % 360} 80% 45%))`,
                  }}
                >{initials(r.name)}</div>
                <div className="phc-who">
                  <div className="phc-name">
                    {r.name}
                    {onFloor.has(r.name) && <span className="phc-onfloor" title="On the floor now">●</span>}
                    {boosted.has(r.name) && <span className="phc-boosted" title="Boosted — personal multiplier live">🚀</span>}
                    {!!r.streak && <span className="phc-streak">🔥{r.streak}</span>}
                  </div>
                  <div className="phc-meta">
                    {fmt(r.meetings)} mtgs · {fmt(r.connects)} connects · {fmt(r.dials)} dials
                    {win !== 'all' && <> · career 🪙 {fmt(lifetime)}</>}
                  </div>
                  <div className="phc-bar"><i style={{ width: `${share}%`, background: `hsl(${hue} 80% 55%)` }} /></div>
                </div>
                <div className="phc-coins">
                  {market?.enabled && walletBy[r.name] && (
                    <span className="phc-wallet" title="Spendable this quarter (The Vault)">
                      💰 {fmt(walletBy[r.name].spendable)}
                    </span>
                  )}
                  {(walletBy[r.name]?.fuel ?? 0) > 0 && (
                    <span className="phc-fuel" title="Fuel this week — dials & connects mint it, /coins boost spends it">
                      ⛽ {fmt(walletBy[r.name].fuel!)}
                    </span>
                  )}
                  <span className="phc-coins-val">{fmt(r.coins)}</span>
                  <span className="phc-coins-unit">🪙</span>
                  {bump && <span className="phc-bump">+{fmt(bump)}</span>}
                </div>
              </div>
            );
          })}
        </div>

        <aside className="phc-side">
          <div className="phc-card">
            <div className="phc-card-title">💰 RECENT JACKPOTS</div>
            {jackpots.length === 0 && <div className="phc-empty-sm">No spot awards in the last 72h.</div>}
            {jackpots.map(a => (
              <div className="phc-jackpot" key={a.id}>
                <div className="phc-jackpot-top">
                  <b>{a.name}</b><span>+{fmt(a.coins)} 🪙</span>
                </div>
                <div className="phc-jackpot-sub">
                  {a.label || a.note || 'spot award'} · {ago(a.at)}
                </div>
              </div>
            ))}
          </div>

          {market?.enabled && (
            <div className="phc-card">
              <div className="phc-card-title">🛍️ THE VAULT SHOP</div>
              {market.catalog.length === 0 && <div className="phc-empty-sm">Catalog is being stocked.</div>}
              {market.catalog.map(it => (
                <div className="phc-jackpot" key={it.key}>
                  <div className="phc-jackpot-top">
                    <b>{it.label}</b><span>{fmt(it.coins)} 🪙</span>
                  </div>
                  <div className="phc-jackpot-sub">
                    {it.poolable
                      ? `team pool — ${fmt(it.pool_total || 0)} / ${fmt(it.coins)} 🪙`
                      : (it.redeem_command || `/coins redeem ${it.code || it.key}`)}
                    {it.teams.length > 0 && ` · ${it.teams.join('/')}`}
                    {it.stock_left !== null && ` · ${it.stock_left} left this month`}
                  </div>
                </div>
              ))}
              <div className="phc-note">
                Redeem from Slack: <b>/coins balance · shop · redeem · pool</b>. Wallets are
                quarterly; career totals and this board never go down when you spend.
              </div>
            </div>
          )}

          <div className="phc-card">
            <div className="phc-card-title">🪙 HOW COINS ARE MINTED</div>
            {/* fed by GET /rules so this card cannot lie when config changes —
                it used to hardcode "25 per connect" months after that rate died */}
            <ul className="phc-rules">
              <li><b>{(rules?.earning?.coins_per_meeting ?? 500).toLocaleString()}</b> per meeting booked — the only coin source</li>
              <li><b>0</b> per dial or connect — activity, not currency (they mint ⛽ Fuel for boosts instead)</li>
              {(rules?.multipliers ?? []).map(m => (
                <li key={m.name}>{m.name}: <b>{m.multiplier}x</b> {m.days_label} {m.start}–{m.end}</li>
              ))}
              {rules?.boosts?.enabled && (
                <li>🚀 personal boosts: <b>{rules.boosts.multiplier}x</b> bought with Fuel, stacks to <b>{rules.boosts.max_total_multiplier}x</b> — <b>/coins boost</b></li>
              )}
              <li>Hosts can mint one-off jackpots by hand (capped)</li>
            </ul>
            {!market?.enabled && (
              <div className="phc-note">
                Board totals are <b>lifetime earned</b>. The Vault (spend coins from Slack)
                opens soon — wallets will be quarterly and this board never goes down.
              </div>
            )}
          </div>
        </aside>
      </div>

      <div className="phc-foot">
        auto-refreshing every {POLL_MS / 1000}s
        {data?.server_time && <> · server {new Date(data.server_time).toLocaleTimeString()}</>}
      </div>
    </div>
  );
};

export default PowerHourCoins;
